import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  RecallResult,
  RecallState,
  RecallServiceError,
  reportDeliveryRecallService,
} from "../services/reportDeliveryRecall.service";

export const reportDeliveryRecallQueryKey = (caseId: string) =>
  ["report-delivery-recall", caseId] as const;

export const REPORT_DELIVERY_RECALL_POLL_INTERVAL_MS = 20_000;

export function resolveReportDeliveryRecallPollInterval(
  state: RecallState | undefined
): number | false {
  return state?.aggregateState === "delivered"
    ? false
    : REPORT_DELIVERY_RECALL_POLL_INTERVAL_MS;
}

export type RecallCountdownPhase =
  | "counting"
  | "checking"
  | "due_now"
  | "unavailable";

function createRequestKey(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (value) => {
    const random = Math.floor(Math.random() * 16);
    const digit = value === "x" ? random : (random % 4) + 8;
    return digit.toString(16);
  });
}

export function useReportDeliveryRecall(
  caseId: string | undefined,
  enabled = true
) {
  const queryClient = useQueryClient();
  const [monotonicNow, setMonotonicNow] = useState(() => performance.now());
  const [opened, setOpened] = useState(false);
  const [requestKey, setRequestKey] = useState<string | null>(null);
  const [result, setResult] = useState<RecallResult | null>(null);
  const [error, setError] = useState<RecallServiceError | null>(null);
  const [isReconciling, setIsReconciling] = useState(false);
  const boundaryCheckedFor = useRef<string | null>(null);
  const serverClockBaseline = useRef<{
    serverTime: string;
    serverMs: number;
    monotonicMs: number;
  } | null>(null);

  const query = useQuery({
    queryKey: reportDeliveryRecallQueryKey(caseId ?? ""),
    queryFn: () =>
      reportDeliveryRecallService.getState(caseId!, requestKey ?? undefined),
    enabled: enabled && !!caseId,
    refetchInterval: (activeQuery) =>
      resolveReportDeliveryRecallPollInterval(activeQuery.state.data),
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  });

  if (
    query.data?.serverTime &&
    serverClockBaseline.current?.serverTime !== query.data.serverTime
  ) {
    serverClockBaseline.current = {
      serverTime: query.data.serverTime,
      serverMs: Date.parse(query.data.serverTime),
      monotonicMs: performance.now(),
    };
  }

  const remainingSeconds = useMemo(() => {
    const scheduledAt = query.data?.earliestPendingScheduledAt;
    const baseline = serverClockBaseline.current;
    if (!scheduledAt || !baseline) return null;
    const elapsedMs = Math.max(0, monotonicNow - baseline.monotonicMs);
    const serverNow = baseline.serverMs + elapsedMs;
    return Math.max(0, Math.ceil((Date.parse(scheduledAt) - serverNow) / 1000));
  }, [monotonicNow, query.data?.earliestPendingScheduledAt]);

  const countdownPhase = useMemo<RecallCountdownPhase>(() => {
    if (remainingSeconds === null) return "unavailable";
    if (remainingSeconds > 0) return "counting";
    return query.isFetching ? "checking" : "due_now";
  }, [query.isFetching, remainingSeconds]);

  const stateError = useMemo(() => {
    if (!query.error) return null;
    if (query.error instanceof RecallServiceError) return query.error;
    return new RecallServiceError(
      "failed",
      "Delivery status could not be loaded. Refresh the case before relying on this status."
    );
  }, [query.error]);

  const mutation = useMutation({
    mutationFn: (reason: string) => {
      if (!caseId || !query.data?.signOffId || !requestKey) {
        throw new RecallServiceError(
          "failed",
          "Recall details are unavailable. Refresh the case and try again."
        );
      }
      return reportDeliveryRecallService.recall(caseId, {
        signOffId: query.data.signOffId,
        requestKey,
        reason,
      });
    },
    onSuccess: (recallResult) => {
      setResult(recallResult);
      setError(null);
      if (caseId) {
        queryClient.invalidateQueries({
          queryKey: reportDeliveryRecallQueryKey(caseId),
        });
        queryClient.invalidateQueries({
          queryKey: ["case-for-pathologist-review", caseId],
        });
        queryClient.invalidateQueries({ queryKey: ["case", caseId] });
        queryClient.invalidateQueries({
          queryKey: ["stain-deletion-eligibility", caseId],
        });
        queryClient.invalidateQueries({ queryKey: ["document-view", caseId] });
        queryClient.invalidateQueries({
          queryKey: ["sign-off-history", caseId],
        });
        queryClient.invalidateQueries({
          queryKey: ["pathologist-review-queue"],
        });
        queryClient.invalidateQueries({
          queryKey: ["pathologist-review-stats"],
        });
      }
    },
    onError: (mutationError) => {
      setError(
        mutationError instanceof RecallServiceError
          ? mutationError
          : new RecallServiceError(
              "unknown",
              "The connection was interrupted. Check the recall outcome before trying again."
            )
      );
    },
  });

  const revertMutation = useMutation({
    mutationFn: () => {
      if (!caseId || !query.data?.signOffId) {
        throw new RecallServiceError(
          "failed",
          "Revert details are unavailable. Refresh the case and try again."
        );
      }
      return reportDeliveryRecallService.revertSignoff(caseId, {
        signOffId: query.data.signOffId,
        requestKey: createRequestKey(),
      });
    },
    onSuccess: () => {
      if (!caseId) return;
      queryClient.invalidateQueries({
        queryKey: reportDeliveryRecallQueryKey(caseId),
      });
      queryClient.invalidateQueries({
        queryKey: ["case-for-pathologist-review", caseId],
      });
      queryClient.invalidateQueries({ queryKey: ["case", caseId] });
      queryClient.invalidateQueries({
        queryKey: ["stain-deletion-eligibility", caseId],
      });
      queryClient.invalidateQueries({ queryKey: ["document-view", caseId] });
      queryClient.invalidateQueries({ queryKey: ["sign-off-history", caseId] });
      queryClient.invalidateQueries({ queryKey: ["pathologist-review-queue"] });
      queryClient.invalidateQueries({ queryKey: ["pathologist-review-stats"] });
    },
  });

  const openModal = useCallback(() => {
    setRequestKey(createRequestKey());
    setResult(null);
    setError(null);
    setOpened(true);
  }, []);

  const closeModal = useCallback(() => {
    if (mutation.isPending || isReconciling) return;
    setOpened(false);
  }, [isReconciling, mutation.isPending]);

  const submitRecall = useCallback(
    async (reason: string) => {
      if (mutation.isPending) return undefined;
      return mutation.mutateAsync(reason).catch(() => undefined);
    },
    [mutation]
  );

  const checkNow = useCallback(async () => {
    if (!caseId || !requestKey || isReconciling) return;
    setIsReconciling(true);
    try {
      const state = await reportDeliveryRecallService.getState(
        caseId,
        requestKey
      );
      queryClient.setQueryData(reportDeliveryRecallQueryKey(caseId), state);
      if (state.reconciledAttempt) {
        setResult(state.reconciledAttempt);
        setError(null);
      }
    } catch {
      setError(
        new RecallServiceError(
          "unknown",
          "The connection was interrupted. Check the recall outcome before trying again."
        )
      );
    } finally {
      setIsReconciling(false);
    }
  }, [caseId, isReconciling, queryClient, requestKey]);

  const revertSignoff = useCallback(async () => {
    if (revertMutation.isPending) return undefined;
    setError(null);
    return revertMutation.mutateAsync().catch((mutationError) => {
      setError(
        mutationError instanceof RecallServiceError
          ? mutationError
          : new RecallServiceError(
              "unknown",
              "The connection was interrupted. Refresh the case to verify the sign-off state."
            )
      );
      return undefined;
    });
  }, [revertMutation]);

  const refetch = query.refetch;

  useEffect(() => {
    if (!requestKey || !query.data?.reconciledAttempt) return;
    setResult(query.data.reconciledAttempt);
    setError(null);
  }, [query.data?.reconciledAttempt, requestKey]);

  useEffect(() => {
    const interval = window.setInterval(
      () => setMonotonicNow(performance.now()),
      1000
    );
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const schedule = query.data?.earliestPendingScheduledAt;
    if (
      schedule &&
      remainingSeconds === 0 &&
      boundaryCheckedFor.current !== schedule
    ) {
      boundaryCheckedFor.current = schedule;
      void refetch();
    }
  }, [query.data?.earliestPendingScheduledAt, refetch, remainingSeconds]);

  return {
    ...query,
    state: query.data,
    stateError,
    remainingSeconds,
    countdownPhase,
    opened,
    requestKey,
    result,
    error,
    isSubmitting: mutation.isPending,
    isReverting: revertMutation.isPending,
    isReconciling,
    openModal,
    closeModal,
    submitRecall,
    checkNow,
    revertSignoff,
  };
}
